import { supabase } from './supabase';
import { v4 as uuidv4 } from 'uuid';

/**
 * Uploads an image to Supabase storage and returns the URL
 * @param userId User ID for organizing files by user
 * @param base64Image Base64 encoded image data
 * @param fileName Original file name or generated name
 * @returns URL of the uploaded image
 * @deprecated Use uploadImageWithUuid instead for the new naming convention
 */
export async function uploadImageToStorage(
  userId: string,
  base64Image: string,
  fileName: string
): Promise<string> {
  try {
    // Generate a unique file name to avoid collisions
    const timestamp = new Date().getTime();
    const fileExt = fileName.split('.').pop() || 'jpg';
    const uniqueFileName = `${timestamp}_${Math.random().toString(36).substring(2, 15)}.${fileExt}`;
    const fullPath = `${userId}/${uniqueFileName}`;

    // Convert base64 to Blob for web
    const byteCharacters = atob(base64Image);
    const byteArrays = [];
    
    for (let offset = 0; offset < byteCharacters.length; offset += 512) {
      const slice = byteCharacters.slice(offset, offset + 512);
      
      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }
      
      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }
    
    const binaryData = new Blob(byteArrays, { type: `image/${fileExt}` });

    // Upload the file to Supabase Storage
    const { data, error } = await supabase.storage
      .from('wardrobe')
      .upload(fullPath, binaryData, {
        contentType: `image/${fileExt}`,
      });

    if (error) {
      throw error;
    }

    // Get the public URL for the uploaded file
    const { data: urlData } = supabase.storage
      .from('wardrobe')
      .getPublicUrl(fullPath);

    return urlData.publicUrl;
  } catch (error) {
    console.error('Error uploading image:', error);
    throw error;
  }
}

/**
 * Converts base64 data to a Blob
 * @param base64Data Base64 encoded image data
 * @param contentType MIME type of the image
 * @returns Blob representation of the image
 */
function base64ToBlob(base64Data: string, contentType: string = 'image/jpeg'): Blob {
  const byteCharacters = atob(base64Data);
  const byteArrays = [];
  
  for (let offset = 0; offset < byteCharacters.length; offset += 512) {
    const slice = byteCharacters.slice(offset, offset + 512);
    
    const byteNumbers = new Array(slice.length);
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }
    
    const byteArray = new Uint8Array(byteNumbers);
    byteArrays.push(byteArray);
  }
  
  return new Blob(byteArrays, { type: contentType });
}

/**
 * Uploads an original image and optional mask to Supabase storage using the new UUID naming convention
 * @param userId User ID for organizing files by user
 * @param base64Image Base64 encoded image data
 * @param fileExt File extension for the image (jpg, png, etc.)
 * @param base64Mask Optional base64 encoded mask data
 * @param imageId Optional existing image ID to use (if not provided, a new UUID will be generated)
 * @param maskId Optional existing mask ID to use (if not provided and mask is present, a new UUID will be generated)
 * @returns Object with image and mask information
 */
export async function uploadImageWithUuid(
  userId: string,
  base64Image: string,
  fileExt: string = 'jpg',
  base64Mask: string | null = null,
  imageId: string = uuidv4(),
  maskId: string | null = base64Mask ? uuidv4() : null
): Promise<{ 
  imageUrl: string; 
  maskUrl: string | null;
  imageId: string;
  maskId: string | null;
}> {
  try {
    // Create image filename using UUID format
    const imageFileName = `${imageId}-image.${fileExt}`;
    const imagePath = `${userId}/${imageFileName}`;
    
    // Convert base64 to Blob
    const contentType = `image/${fileExt}`;
    const imageBlob = base64ToBlob(base64Image, contentType);
    
    // Upload the original image
    const { error: imageError } = await supabase.storage
      .from('wardrobe')
      .upload(imagePath, imageBlob, {
        contentType,
        upsert: true, // Allow overwriting if the file exists
      });

    if (imageError) {
      throw imageError;
    }
    
    // Get the public URL for the image
    const { data: imageUrlData } = supabase.storage
      .from('wardrobe')
      .getPublicUrl(imagePath);
    
    let maskUrl: string | null = null;
    
    // If mask is provided, upload it with the related naming format
    if (base64Mask && maskId) {
      // Create mask filename using both UUIDs
      const maskFileName = `${imageId}-image-${maskId}-mask.${fileExt}`;
      const maskPath = `${userId}/${maskFileName}`;
      
      // Convert mask base64 to Blob
      const maskBlob = base64ToBlob(base64Mask, contentType);
      
      // Upload the mask
      const { error: maskError } = await supabase.storage
        .from('wardrobe')
        .upload(maskPath, maskBlob, {
          contentType,
          upsert: true, // Allow overwriting if the file exists
        });
      
      if (maskError) {
        console.error('Error uploading mask:', maskError);
        // Continue without the mask if there's an error
      } else {
        // Get the public URL for the mask
        const { data: maskUrlData } = supabase.storage
          .from('wardrobe')
          .getPublicUrl(maskPath);
        
        maskUrl = maskUrlData.publicUrl;
      }
    }
    
    return {
      imageUrl: imageUrlData.publicUrl,
      maskUrl,
      imageId,
      maskId,
    };
  } catch (error) {
    console.error('Error uploading image with UUID format:', error);
    throw error;
  }
}

/**
 * Converts a file or data URL to a base64 encoded string
 * @param fileOrDataUrl File object or data URL
 * @returns Base64 encoded image data (without the data URL prefix)
 */
export async function fileToBase64(fileOrDataUrl: File | string): Promise<string> {
  try {
    // If it's already a data URL, extract the base64 part
    if (typeof fileOrDataUrl === 'string' && fileOrDataUrl.startsWith('data:')) {
      const commaIndex = fileOrDataUrl.indexOf(',');
      if (commaIndex !== -1) {
        return fileOrDataUrl.substring(commaIndex + 1);
      }
      throw new Error('Invalid data URL format');
    }
    
    // Otherwise, if it's a File object, read it as a data URL
    if (fileOrDataUrl instanceof File) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result as string;
          const base64 = result.substring(result.indexOf(',') + 1);
          resolve(base64);
        };
        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsDataURL(fileOrDataUrl);
      });
    }
    
    // If it's a URL string, fetch it and convert to base64
    if (typeof fileOrDataUrl === 'string') {
      const response = await fetch(fileOrDataUrl);
      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result as string;
          const base64 = result.substring(result.indexOf(',') + 1);
          resolve(base64);
        };
        reader.onerror = () => reject(new Error('Failed to read fetched image'));
        reader.readAsDataURL(blob);
      });
    }
    
    throw new Error('Unsupported input type');
  } catch (error) {
    console.error('Error converting to base64:', error);
    throw error;
  }
} 